import { TILE_TERRAIN_IDS, type TileTerrain } from "@dawn/engine";
import { DEFAULT_CORE_BALANCE, type CoreBalance } from "./coreBalance.ts";

export const CORE_BALANCE_OVERRIDE_VERSION = 1 as const;

/** Deep-partial patch applied onto defaults (nested objects merge; arrays replace). */
export type CoreBalancePatch = {
  [K in keyof CoreBalance]?: CoreBalance[K] extends Array<infer _U>
    ? CoreBalance[K]
    : CoreBalance[K] extends Record<string, unknown>
      ? Partial<CoreBalance[K]>
      : CoreBalance[K];
};

const CORE_KEYS = new Set<string>(Object.keys(DEFAULT_CORE_BALANCE));

const TERRAIN_KEYS = new Set<string>(TILE_TERRAIN_IDS);
const SEASON_KEYS = new Set(["spring", "summer", "autumn", "winter"]);
const DAY_NIGHT_KEYS = new Set(["day", "dusk", "dawn", "night"]);
const AMBIENT_LEVEL_KEYS = new Set(["none", "low", "medium"]);

const NESTED_KEY_SETS: Partial<Record<keyof CoreBalance, Set<string>>> = {
  terrainFoodProduction: TERRAIN_KEYS,
  terrainBaseWater: TERRAIN_KEYS,
  terrainAmbientPredatorBase: TERRAIN_KEYS,
  seasonWaterMultiplier: SEASON_KEYS,
  seasonAmbientPredatorModifier: SEASON_KEYS,
  dayNightAmbientPredatorModifier: DAY_NIGHT_KEYS,
  ambientPredatorLevelMaxScore: AMBIENT_LEVEL_KEYS,
};

export function resolveCoreBalance(
  defaults: CoreBalance,
  patch: CoreBalancePatch | undefined,
): CoreBalance {
  if (patch === undefined) {
    const cloned = structuredClone(defaults);
    assertValidCoreBalance(cloned);
    return cloned;
  }

  const result = structuredClone(defaults);
  const writable = result as unknown as Record<string, unknown>;
  for (const key of Object.keys(patch) as Array<keyof CoreBalance>) {
    const value = patch[key];
    if (value === undefined) {
      continue;
    }
    const current = result[key];
    if (
      Array.isArray(value) ||
      typeof value !== "object" ||
      value === null ||
      typeof current !== "object" ||
      current === null ||
      Array.isArray(current)
    ) {
      writable[key] = structuredClone(value);
    } else {
      writable[key] = {
        ...(current as Record<string, unknown>),
        ...(value as Record<string, unknown>),
      };
    }
  }
  assertValidCoreBalance(result);
  return result;
}

export function parseCoreBalanceOverride(json: string): CoreBalancePatch {
  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch {
    throw new Error("Invalid core balance override JSON");
  }

  if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
    throw new Error("Invalid core balance override shape");
  }

  const record = parsed as Record<string, unknown>;
  for (const key of Object.keys(record)) {
    if (key !== "version" && key !== "core") {
      throw new Error(`Unknown key: ${key}`);
    }
  }

  if (record.version !== CORE_BALANCE_OVERRIDE_VERSION) {
    throw new Error(
      `Unsupported core balance override version: ${String(record.version)}`,
    );
  }

  if (
    typeof record.core !== "object" ||
    record.core === null ||
    Array.isArray(record.core)
  ) {
    throw new Error("Invalid core balance override shape");
  }

  return parseCorePatch(record.core as Record<string, unknown>);
}

function parseCorePatch(core: Record<string, unknown>): CoreBalancePatch {
  const patch: CoreBalancePatch = {};

  for (const key of Object.keys(core)) {
    if (!CORE_KEYS.has(key)) {
      throw new Error(`Unknown key: ${key}`);
    }

    const value = core[key];
    const nestedKeys = NESTED_KEY_SETS[key as keyof CoreBalance];

    if (key === "terrainSpawnWeights") {
      patch.terrainSpawnWeights = parseSpawnWeights(value);
      continue;
    }

    if (nestedKeys) {
      if (typeof value !== "object" || value === null || Array.isArray(value)) {
        throw new Error(`Invalid value for ${key}`);
      }
      const nested: Record<string, number> = {};
      for (const [nestedKey, nestedValue] of Object.entries(
        value as Record<string, unknown>,
      )) {
        if (!nestedKeys.has(nestedKey)) {
          throw new Error(`Unknown key: ${key}.${nestedKey}`);
        }
        if (typeof nestedValue !== "number" || !Number.isFinite(nestedValue)) {
          throw new Error(`Invalid value for ${key}.${nestedKey}`);
        }
        nested[nestedKey] = nestedValue;
      }
      (patch as unknown as Record<string, unknown>)[key] = nested;
      continue;
    }

    if (typeof value !== "number" || !Number.isFinite(value)) {
      throw new Error(`Invalid value for ${key}`);
    }
    (patch as unknown as Record<string, unknown>)[key] = value;
  }

  return patch;
}

function parseSpawnWeights(
  value: unknown,
): CoreBalance["terrainSpawnWeights"] {
  if (!Array.isArray(value)) {
    throw new Error("Invalid value for terrainSpawnWeights");
  }
  if (value.length === 0) {
    throw new Error("Invalid value for terrainSpawnWeights");
  }
  return value.map((entry, index) => {
    if (typeof entry !== "object" || entry === null || Array.isArray(entry)) {
      throw new Error(`Invalid value for terrainSpawnWeights[${index}]`);
    }
    const record = entry as Record<string, unknown>;
    for (const key of Object.keys(record)) {
      if (key !== "terrain" && key !== "weight") {
        throw new Error(`Unknown key: terrainSpawnWeights[${index}].${key}`);
      }
    }
    if (
      typeof record.terrain !== "string" ||
      !TERRAIN_KEYS.has(record.terrain)
    ) {
      throw new Error(`Invalid value for terrainSpawnWeights[${index}].terrain`);
    }
    if (typeof record.weight !== "number" || !Number.isFinite(record.weight)) {
      throw new Error(`Invalid value for terrainSpawnWeights[${index}].weight`);
    }
    return {
      terrain: record.terrain as TileTerrain,
      weight: record.weight,
    };
  });
}

function assertValidCoreBalance(balance: CoreBalance): void {
  if (balance.fireEventProbability < 0 || balance.fireEventProbability > 1) {
    throw new Error("Invalid value for fireEventProbability");
  }
  if (balance.fireDurationTurnsMin > balance.fireDurationTurnsMax) {
    throw new Error(
      "Invalid value for fireDurationTurnsMin/fireDurationTurnsMax",
    );
  }
  if (balance.terrainSpawnWeights.length === 0) {
    throw new Error("Invalid value for terrainSpawnWeights");
  }
}
